import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { SOverlaySpinner } from "../SSpinner";
export const BaseButtonStyles = `
  flex
  flex-row
  gap-2
  justify-center
  items-center
  transition
  transition-all
  ease-in-out
  duration-100
  font-medium
  hover:opacity-90
  active:scale-90
  transform-gpu
`;
const VariantStyles = {
    primary: "bg-[rgba(116,255,241,0.1)] text-brand-primary",
    secondary: "bg-[rgba(163,163,163,0.1)] text-white",
    juicy: "bg-accent-light text-grayscale-darkest",
    tertiary: "cursor-pointer px-3 !font-normal h-[32px] transition hover:!text-white/50 bg-grayscale-dark border border-white/10 !rounded-md flex flex-row items-center justify-between !text-[13px]"
};
const SizeStyles = {
    sm: "rounded-lg py-1 px-2 min-h-[26px] text-[11px]",
    md: "rounded-xl py-2 px-4 text-sm",
    lg: "rounded-lg py-1 px-2 text-base",
    xl: "rounded-lg py-1 px-2 text-lg"
};
const DisabledStyles = `
  pointer-events-none
  !opacity-50
`;
import { DynamicLink } from "../internal/DynamicLink";
import useHotKey, { formatKeyboardShortcut, useCommandKey } from "../hooks/useKeyboardShortcut";
import STooltip from "../Tooltip";
import useWindowSize from "../hooks/useWindowSize";
const ButtonLink = ({ href, linkProps, children, ...rest })=>{
    return /*#__PURE__*/ _jsx(DynamicLink, {
        href: href,
        ...linkProps,
        ...rest,
        children: children
    });
};
const compileButtonStyles = ({ className, variant = "primary", size = "md", isMinimal = false, disabled = false })=>{
    const variantStyles = VariantStyles[variant];
    const sizeStyles = SizeStyles[size];
    const minimalStyles = isMinimal ? "!bg-transparent" : "";
    const disabledStyles = disabled ? DisabledStyles : "";
    return `${BaseButtonStyles} ${variantStyles} ${sizeStyles} ${minimalStyles} ${disabledStyles} ${className} cursor-pointer`;
};
const SButton = ({ children, /**
   * Use `className` as a css override. Some class names may require the "!" tag
   * before the class name to fully override the base button styles or additional
   * styles that are added due to its variant and/or states.
   */ className, variant = "primary", hotkey: keyboardShortcut, hotkeyOptions, tooltip, size = "md", isMinimal = false, disabled = false, type = "button", onClick, onCommandClick, isLoading: _isLoading, href, linkProps, target, ...props })=>{
    const styles = compileButtonStyles({
        variant,
        size,
        isMinimal,
        disabled,
        className
    });
    const [animateButtonPress, setAnimateButtonPress] = useState(false);
    const [didReturnPromise, setDidReturnPromise] = useState(null);
    const isLoading = _isLoading ?? didReturnPromise;
    const { isCommandPressed } = useCommandKey();
    const screenSize = useWindowSize();
    const click = async (e)=>{
        const onClickUsing = isCommandPressed && onCommandClick !== undefined ? onCommandClick : onClick;
        if (onClickUsing) {
            // If we're currently awaiting a promise
            if (didReturnPromise) {
                return;
            }
            if (e) {
                e.preventDefault();
            }
            const onClickResult = onClickUsing(e);
            if (onClickResult instanceof Promise) {
                if (e) e.preventDefault() // Prevent the default behavior of the button
                ;
                setDidReturnPromise(true);
                await onClickResult;
                setDidReturnPromise(null);
            }
        }
    };
    useHotKey({
        hotkey: keyboardShortcut,
        action: async ()=>{
            setAnimateButtonPress(true);
            await click(undefined);
            setTimeout(()=>{
                setAnimateButtonPress(false);
            }, 100);
        },
        ...hotkeyOptions
    });
    if (typeof href !== "undefined") {
        return /*#__PURE__*/ _jsx(ButtonLink, {
            href: href,
            linkProps: linkProps,
            className: styles,
            onClick: onClick,
            target,
            children: children
        });
    }
    return /*#__PURE__*/ _jsx("button", {
        type: type,
        onClick: click,
        className: styles,
        ...props,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "relative",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        opacity: isLoading ? 0 : 1,
                        transform: isLoading ? "scale(0)" : animateButtonPress ? "scale(0.9)" : "scale(1.0)",
                        transition: "transform 0.1s ease-in-out, opacity 0.1s ease-in-out"
                    },
                    className: " flex flex-row gap-2 justify-center items-center",
                    children: (keyboardShortcut || tooltip) && !tooltip?.disabled && (screenSize?.width || 100) > 768 ? /*#__PURE__*/ _jsx(STooltip, {
                        zIndex: 9999999,
                        delay: tooltip?.delay === undefined ? 500 : tooltip.delay,
                        placement: tooltip?.placement || "right",
                        content: /*#__PURE__*/ _jsx("div", {
                            className: "!max-w-[200px] inline",
                            children: /*#__PURE__*/ _jsxs("div", {
                                className: "-my-3 -mx-1 inline-flex items-center justify-center gap-3 ",
                                children: [
                                    tooltip?.content && // if tooltip is a string
                                    (typeof tooltip.content === "string" ? /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-normal pl-1",
                                        children: tooltip.content
                                    }) : tooltip.content),
                                    keyboardShortcut && /*#__PURE__*/ _jsx("div", {
                                        className: "flex flex-row items-center justify-center gap-1",
                                        children: formatKeyboardShortcut(keyboardShortcut).map((key, index)=>/*#__PURE__*/ _jsx("div", {
                                                className: "text-white texm-xs bg-white/10 border border-white/10 font-medium tracking-wider rounded-md flex flex-row items-center justify-center w-[22px] h-[22px]",
                                                children: index === 0 ? /*#__PURE__*/ _jsx("span", {
                                                    children: key
                                                }) : /*#__PURE__*/ _jsx("span", {
                                                    className: "font-mono",
                                                    children: key
                                                })
                                            }, index))
                                    })
                                ]
                            })
                        }),
                        children: children
                    }) : children
                }),
                /*#__PURE__*/ _jsx("div", {
                    style: {
                        position: "absolute",
                        pointerEvents: "none",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: !isLoading ? 0 : 1,
                        transform: !isLoading ? "scale(0)" : "scale(1.0)",
                        transition: "transform 0.2s ease-in-out, opacity 0.2s ease-in-out"
                    },
                    children: /*#__PURE__*/ _jsx(SOverlaySpinner, {
                        overlayColor: "rgba(0,0,0,0)"
                    })
                })
            ]
        })
    });
};
SButton.compileButtonStyles = compileButtonStyles;
export { SButton };
